// src/pages/Auth/ForgotPassword.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, KeyRound, Lock, Eye, EyeOff, Check, ChevronLeft, ChevronRight, Send, Clock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { BRAND_CONFIG, getLogoSrc } from '../../config/brandConfig';
import { useAppTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ThemeToggle, LanguageToggle } from '../../components/common/UiControls';

const inputBase = 'w-full h-12 px-4 rounded-xl border text-sm transition-all outline-none bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#24299d]/20 focus:border-[#24299d] dark:focus:border-[#5a61e0]';
const inputError = 'border-red-500';
const inputOk = 'border-gray-200 dark:border-white/10';
const labelClass = 'block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { darkMode } = useAppTheme();
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showCPw, setShowCPw] = useState(false);
  const [email, setEmail] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [canResend, setCanResend] = useState(true);

  const logoSrc = getLogoSrc(darkMode);
  const v = t('auth.validation');

  const stepSchemas = [
    yup.object({
      email: yup.string().required(v.emailRequired).email(v.emailInvalid).matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, v.emailInvalid),
    }),
    yup.object({
      verificationCode: yup.string().required(v.codeRequired).length(6, v.codeLength).matches(/^[0-9]{6}$/, v.codeDigitsOnly),
    }),
    yup.object({
      newPassword: yup.string().required(v.newPasswordRequired).min(8, v.newPasswordMin).matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, v.newPasswordStrength),
      confirmPassword: yup.string().required(v.confirmRequired).oneOf([yup.ref('newPassword')], v.passwordsDoNotMatch),
    }),
  ];

  const steps = [
    { label: t('auth.step.email'), icon: Mail },
    { label: t('auth.step.verifyCode'), icon: KeyRound },
    { label: t('auth.step.newPassword'), icon: Lock },
  ];

  useEffect(() => {
    let interval;
    if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) { setCanResend(true); return 0; }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timeLeft]);

  const { register, handleSubmit, formState: { errors, isValid } } = useForm({
    resolver: yupResolver(stepSchemas[step]),
    mode: 'onChange',
    defaultValues: { email: '', verificationCode: '', newPassword: '', confirmPassword: '' },
  });

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (step === 0) {
        await new Promise((r) => setTimeout(r, 1500));
        setEmail(data.email);
        setStep(1);
        setTimeLeft(300);
        setCanResend(false);
        window.showToast?.(t('auth.codeSentToEmail'), 'success');
      } else if (step === 1) {
        await new Promise((r) => setTimeout(r, 1200));
        if (data.verificationCode === '123456') {
          setStep(2);
          window.showToast?.(t('auth.codeVerifiedSetPassword'), 'success');
        } else {
          throw new Error(t('auth.codeIncorrect'));
        }
      } else {
        await new Promise((r) => setTimeout(r, 1500));
        window.showToast?.(t('auth.passwordUpdatedMsg'), 'success');
        navigate('/login', { state: { message: t('auth.passwordUpdatedSignIn') } });
      }
    } catch (err) {
      window.showToast?.(err.message || t('auth.serverError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    setTimeLeft(300);
    setCanResend(false);
    setLoading(false);
    window.showToast?.(t('auth.newCodeSent'), 'success');
  };

  const StepContent = () => {
    if (step === 0) return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">{t('auth.enterEmailForReset')}</p>
        <div>
          <label className={labelClass}>{t('auth.email')}</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="email" {...register('email')} placeholder={t('auth.emailPlaceholder')} className={`${inputBase} pl-10 ${errors.email ? inputError : inputOk}`} />
          </div>
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
        </div>
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-sm text-blue-700 dark:text-blue-300">
          {t('auth.codeValidFor5Min')}
        </div>
      </div>
    );

    if (step === 1) return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">{t('auth.enterCodeSentTo', { email })}</p>
        <div>
          <label className={labelClass}>{t('auth.verificationCode')}</label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input {...register('verificationCode')} maxLength={6} placeholder="000000" className={`${inputBase} pl-10 text-center tracking-widest text-lg font-bold ${errors.verificationCode ? inputError : inputOk}`} />
          </div>
          {errors.verificationCode && <p className="mt-1 text-xs text-red-500">{errors.verificationCode.message}</p>}
        </div>
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-xl">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Clock size={16} className="text-gray-400" />
            {timeLeft > 0
              ? <span>{t('auth.expiresIn', { time: formatTime(timeLeft) })}</span>
              : <span>{t('auth.codeExpired')}</span>}
          </div>
          <button type="button" onClick={handleResend} disabled={!canResend || loading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#24299d] text-[#24299d] hover:bg-[#24299d]/5 disabled:opacity-40 transition-colors">
            <Send size={13} /> {t('auth.resend')}
          </button>
        </div>
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl text-sm text-amber-700 dark:text-amber-300">
          {t('auth.checkSpam')}
        </div>
      </div>
    );

    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">{t('auth.createStrongPassword')}</p>
        <div>
          <label className={labelClass}>{t('auth.newPasswordLabel')}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type={showPw ? 'text' : 'password'} {...register('newPassword')} placeholder="••••••••" className={`${inputBase} pl-10 pr-12 ${errors.newPassword ? inputError : inputOk}`} />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.newPassword
            ? <p className="mt-1 text-xs text-red-500">{errors.newPassword.message}</p>
            : <p className="mt-1 text-xs text-gray-400">{t('auth.passwordHint')}</p>}
        </div>
        <div>
          <label className={labelClass}>{t('auth.confirmPassword')}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type={showCPw ? 'text' : 'password'} {...register('confirmPassword')} placeholder="••••••••" className={`${inputBase} pl-10 pr-12 ${errors.confirmPassword ? inputError : inputOk}`} />
            <button type="button" onClick={() => setShowCPw(!showCPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showCPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>}
        </div>
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-sm text-green-700 dark:text-green-300">
          {t('auth.passwordTip')}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-4 font-sans">
      <div className="w-full max-w-md">
        <div className="flex justify-end items-center gap-2 mb-4">
          <LanguageToggle />
          <ThemeToggle />
        </div>

        <div className="bg-white dark:bg-[#1C1C1E] rounded-3xl shadow-xl p-7 border border-gray-100 dark:border-white/10">
          <div className="text-center mb-6">
            <img src={logoSrc} alt={BRAND_CONFIG.name} className="w-3/4 h-16 object-contain mx-auto mb-4" onError={(e) => { e.target.style.display = 'none'; }} />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('auth.forgotPasswordTitle')}</h2>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              {steps.map((s, i) => {
                const Icon = s.icon;
                const done = i < step;
                const active = i === step;
                return (
                  <React.Fragment key={s.label}>
                    <div className="flex flex-col items-center gap-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${done ? 'bg-green-500' : active ? 'bg-[#24299d] shadow-md shadow-[#24299d]/30' : 'bg-gray-200 dark:bg-white/10'}`}>
                        {done ? <Check size={14} className="text-white" /> : <Icon size={14} className={active ? 'text-white' : 'text-gray-400'} />}
                      </div>
                      <span className={`text-[10px] font-semibold ${active ? 'text-[#24299d]' : done ? 'text-green-500' : 'text-gray-400'}`}>{s.label}</span>
                    </div>
                    {i < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 mb-4 rounded-full transition-all ${i < step ? 'bg-green-500' : 'bg-gray-200 dark:bg-white/10'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
            <StepContent />
            <div className="flex items-center justify-between mt-6">
              <button type="button" onClick={() => step === 0 ? navigate('/login') : setStep((s) => s - 1)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                <ChevronLeft size={16} /> {step === 0 ? t('auth.signIn') : t('common.back')}
              </button>
              <button type="submit" disabled={loading || !isValid} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed ${step === 2 ? 'bg-green-600 hover:bg-green-700 shadow-green-600/20' : 'bg-[#24299d] hover:bg-[#1b2080] shadow-[#24299d]/20'}`}>
                {loading
                  ? t('auth.processing')
                  : step === 2
                    ? <><Check size={16} /> {t('auth.updatePassword')}</>
                    : step === 1
                      ? <>{t('auth.verifyCodeBtn')} <ChevronRight size={16} /></>
                      : <>{t('auth.sendCode')} <ChevronRight size={16} /></>}
              </button>
            </div>
          </form>

          <div className="mt-5 pt-5 border-t border-gray-100 dark:border-white/10 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('auth.havingTrouble')}{' '}
              <button type="button" onClick={() => window.open('mailto:support@fidost.com')} className="font-semibold text-[#24299d] hover:text-[#1b2080] dark:text-[#818cf8]">
                {t('auth.contactSupport')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
